/**
 * DEV-3224 (TDT-3483) — sunucuda hazırlanan rapor Excel'inin durumunu yoklar.
 *
 * Aynı yoklama mantığı üç yerde kopyalanmıştı (RaporDetayView bir, AI sihirbazı
 * iki) ve üçünde de aynı iki kusur vardı:
 *
 *  1. "bulunamadi" ilk yoklamada HATA sayılıyordu. Oysa kuyruğu tüketen işçi
 *     cron ile dakikada bir kalkıyor; iş sırasını bekliyor olabilir.
 *  2. Yoklamanın durma koşulu yoktu — iş hiç başlamazsa tarayıcı süresiz
 *     yokluyordu. Mesaj da yanıltıcıydı: "tekrar deneyin" demek kuyruğa bir iş
 *     daha eklemekten başka işe yaramıyordu.
 *
 * Kopyayı çoğaltmak yerine tek yere taşındı; bir dahaki düzeltme üç yeri
 * aramayı gerektirmesin (aynı uyarıyı üç yere yazmak, otomatikleştirmenin
 * yerine geçmez).
 */

/**
 * DEV-3350: eski işçi tek süreç (crontab `flock -n`) ve iş en çok 600 sn
 * sürüyor; 17 dk hem kuyrukta bekleyişi hem üretimi kapsar.
 */
const TAVAN_MS = 17 * 60 * 1000;
const ARALIK_MS = 3000;

export interface SunucuExcelYoklamaKancalari {
  /** Durumu soran çağrı; 'hazir' | 'hata' | 'bulunamadi' | 'hazirlaniyor' | 'kuyrukta' döner. */
  durumSor: () => Promise<string>;
  /** Dosya hazır. */
  hazir: () => void;
  /** İş kalıcı olarak başarısız oldu. */
  hata: (mesaj: string) => void;
  /** Tavana ulaşıldı; iş hâlâ sürüyor olabilir. */
  zamanAsimi: (mesaj: string) => void;
  /**
   * DEV-3350 — isteğe bağlı: `true` dönerse yoklama durur. Çağıran bileşen
   * ekrandan çıktığında (`beforeDestroy`) bunu `true` yapar; yok edilmiş
   * bileşene artık durum sorusu gitmez. Verilmezse yoklama eskisi gibi
   * yalnız hazır/hata/tavan ile biter.
   */
  durduruldu?: () => boolean;
}

const HATA_METNI = 'Excel üretilemedi. Bir sorun oluştu, lütfen yöneticinize bildirin.';
const TAVAN_METNI = 'Excel hâlâ hazır değil. Hazırlanmaya devam ediyor olabilir; birazdan tekrar bakın.';

/**
 * DEV-3350 — yoklamanın nasıl bittiği, dönüş değeriyle. Kancalar (hazir / hata /
 * zamanAsimi) eskisi gibi çağrılır; sonuç onlara ek bilgi taşır:
 *  - `sonDurum`: son yoklamada sunucunun söylediği durum.
 *  - `istekHatasi`: durum sorusunun kendisi düştü (ağ kopması veya 5xx). Eskiden
 *    bu durumda hiçbir kanca çağrılmıyor, yoklama sessizce duruyor ve çağıran
 *    taraf bunu hiç öğrenemiyordu. Artık sonuç olarak döner (`hata` alanıyla).
 *    DEV-3350 A4: mesajı ÇAĞIRAN gösterir — yanıtlı 5xx'i genel kapı
 *    (StatusCodeHelper) gösterir, ama yanıtsız (ağ) hatada genel kapı sessiz
 *    kalır; onu çağıran kendi toast'ıyla bildirmek zorundadır ("axios eklentisi
 *    zaten gösterir" varsayımı yanıtsız hata için YANLIŞTI).
 */
export type SunucuExcelYoklamaSonucu =
  | { tur: 'hazir'; sonDurum: string }
  | { tur: 'hata'; sonDurum: string }
  | { tur: 'zamanAsimi'; sonDurum: string }
  | { tur: 'istekHatasi'; sonDurum: string | null; hata: unknown }
  | { tur: 'iptal'; sonDurum: string | null };

export function sunucuExcelYokla(
  kancalar: SunucuExcelYoklamaKancalari,
  gecenMs: number = 0,
  // DEV-3350: yoklama aralığı artık çağırandan. Eski yol 3000 ms'de bir yoklar
  // (varsayılan); yeni servis yolu 5000 ms kullanır.
  aralikMs: number = ARALIK_MS,
): Promise<SunucuExcelYoklamaSonucu> {
  // Tek söz, yoklama boyunca açık kalır; adımlar söz zinciri kurmaz (340 adımlık
  // iç içe zincir, sonucun çözülmesini adım sayısı kadar geciktirirdi).
  return new Promise((coz) => {
    const adim = (gecen: number, oncekiDurum: string | null): void => {
      setTimeout(() => {
        // DEV-3350: ekrandan çıkıldıysa durum sorusu bile gönderme; yok edilmiş
        // bileşene istek atmanın anlamı yok.
        if (kancalar.durduruldu && kancalar.durduruldu()) {
          coz({tur: 'iptal', sonDurum: oncekiDurum});
          return;
        }
        kancalar.durumSor().then((durum: string) => {
          if (durum === 'hazir') {
            kancalar.hazir();
            coz({tur: 'hazir', sonDurum: durum});
            return;
          }
          if (durum === 'hata') {
            kancalar.hata(HATA_METNI);
            coz({tur: 'hata', sonDurum: durum});
            return;
          }
          // "bulunamadi" dahil diğer her durum: iş kuyrukta sırasını bekliyor olabilir.
          const yeniGecen = gecen + aralikMs;
          if (yeniGecen >= TAVAN_MS) {
            kancalar.zamanAsimi(TAVAN_METNI);
            coz({tur: 'zamanAsimi', sonDurum: durum});
            return;
          }
          adim(yeniGecen, durum);
        }, (hata: unknown) => {
          coz({tur: 'istekHatasi', sonDurum: oncekiDurum, hata});
        });
      }, aralikMs);
    };
    adim(gecenMs, null);
  });
}

/**
 * DEV-3396 (TDT-3483) — indirme yardımcısı burada DEĞİL, `sunucuExcelIndirme.ts`'te
 * duruyor ve oradan re-export ediliyor.
 *
 * Neden re-export: bu fonksiyonun bir kopyası uzun süre bu dosyada da yaşıyordu ve
 * iki kopya ayrı ayrı davranıyordu (eskisi `onloadend` + `onerror` yüzünden okuma
 * hatasında hata mesajını İKİ kez gösteriyor, `revokeObjectURL`'ü `click()` daha
 * devreye girmeden çağırıyordu). Tek uygulama `sunucuExcelIndirme.ts`; buradaki
 * satır yalnız mevcut import'ları kırmamak için duruyor — davranış arayan biri
 * artık tek yere bakmak zorunda.
 */
export {sunucuExcelKaydet} from "@/utils/sunucuExcelIndirme";
